/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import TransactionalMessageModel, {
  SettingsKey,
  TransactionalMessageChannelType,
} from 'cio/models/transactional-message';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';

interface TransactionalMessageSettingsArgs {
  message: TransactionalMessageModel;
  readOnly: boolean;
  triggerNameValidationErrors: string[];
  triggerNameSaveButtonVisible: boolean;
  updateSetting?: (key: string, value: string) => void;
}

type MessageSetting = {
  key: SettingsKey;
  label: string;
  tooltip?: string;
  disabled?: boolean;
  disabledReason?: string;
};

export default class TransactionalMessageSettings extends Component<TransactionalMessageSettingsArgs> {
  @tracked originalTriggerName!: string;
  @tracked _triggerNameValidationErrors: string[] = [];

  @computed('args.triggerNameValidationErrors', '_triggerNameValidationErrors')
  get triggerNameValidationErrors() {
    if (this.args.triggerNameSaveButtonVisible) {
      return [...this._triggerNameValidationErrors];
    }

    return this.args.triggerNameValidationErrors;
  }

  constructor(owner: unknown, args: TransactionalMessageSettingsArgs) {
    super(owner, args);
    this.originalTriggerName = this.args.message.triggerName;
  }

  @computed('args.message.triggerName', 'originalTriggerName')
  get triggerNameChanged() {
    return this.args.message.triggerName !== this.originalTriggerName;
  }

  updateTriggerNameTask = dropTask(async () => {
    if (this.args.updateSetting == null) {
      return;
    }

    this.resetLocalTriggerNameValidationErrors();
    try {
      await this.args.updateSetting('triggerName', this.args.message.triggerName);
      this.originalTriggerName = this.args.message.triggerName;
    } catch (e) {
      let [error] = e.errors;
      let detail = (error?.detail ?? '') as string;
      if (error?.status === '400' && detail.toLowerCase().includes('trigger name')) {
        this._triggerNameValidationErrors = [detail];
      } else {
        throw e;
      }
    }
  });

  @action
  resetLocalTriggerNameValidationErrors(value?: string) {
    if (value != null) {
      this.args.message.set('triggerName', value);
    }

    this._triggerNameValidationErrors = [];
  }

  @computed('args.message.{hideMessageBody,queueDrafts}')
  get settings(): MessageSetting[] {
    let { message } = this.args;

    if (this.isPush) {
      return [
        {
          label: 'Send to unsubscribed people?',
          key: 'sendToUnsubscribed',
        },
        {
          label: 'Protect sensitive data (e.g. password reset tokens) by disabling message retention.',
          key: 'hideMessageBody',
          tooltip:
            'This will prevent the message body from displaying in Customer.io when viewing sent deliveries. Hiding the message body is helpful for obfuscating sensitive content, like password reset tokens.',
          disabled: message.queueDrafts,
          get disabledReason() {
            if (message.queueDrafts) {
              return 'Message retention cannot be disabled to protect sensitive data when messages are queued as drafts.';
            }

            return '';
          },
        },
        {
          label: 'Queue messages as drafts?',
          key: 'queueDrafts',
          tooltip:
            'This will generate a draft for every triggered message. You can review these messages under the "Drafts" tab and decide whether to send them or delete them.',
          disabled: message.hideMessageBody,
          get disabledReason() {
            if (message.hideMessageBody) {
              return 'Messages cannot be queued as drafts when message retention is disabled to protect sensitive data.';
            }

            return '';
          },
        },
      ];
    }

    return [
      {
        label: 'Send to unsubscribed people?',
        key: 'sendToUnsubscribed',
      },
      {
        label: `Enable ${isDeliveryMeasureEnabledForChannel('opened', 'email') ? 'open and ' : ''}link tracking?`,
        key: 'linkTracking',
        tooltip:
          "Enable link tracking to generate click metrics for any links in your message. <a target='_blank' rel='noopener' href='https://customer.io/docs/link-tracking'>Learn more about link tracking.</a>",
      },
      {
        label: 'Protect sensitive data (e.g. password reset tokens) by disabling message retention.',
        key: 'hideMessageBody',
        tooltip:
          'This will prevent the message body from displaying in Customer.io when viewing sent deliveries. Hiding the message body is helpful for obfuscating sensitive content, like password reset tokens.',
        disabled: message.queueDrafts,
        get disabledReason() {
          if (message.queueDrafts) {
            return 'Message retention cannot be disabled to protect sensitive data when messages are queued as drafts.';
          }

          return '';
        },
      },
      {
        label: 'Queue messages as drafts?',
        key: 'queueDrafts',
        tooltip:
          'This will generate a draft for every triggered message. You can review these messages under the "Drafts" tab and decide whether to send them or delete them.',
        disabled: message.hideMessageBody,
        get disabledReason() {
          if (message.hideMessageBody) {
            return 'Messages cannot be queued as drafts when message retention is disabled to protect sensitive data.';
          }

          return '';
        },
      },
    ];
  }

  get isPush() {
    return this.args.message.type === TransactionalMessageChannelType.Push;
  }
}
